import { find, get, pick } from 'lodash-es';
import { LEASE, PURCHASE } from 'site-modules/shared/constants/car-buying';
import { getTradeInDetails } from 'site-modules/shared/utils/car-buying/calculator-util';
import { DEFAULT_PHOTO } from 'site-modules/shared/utils/inventory-image-utils';
import { getEdmundsSuggestedPrice } from 'site-modules/shared/utils/inventory-utils/prices';
import { getTotalRebates } from 'site-modules/shared/components/inventory/utils/get-total-rebates';

/**
 * Calculated SOP for deal.
 * @param {object} deal
 * @param {boolean} isPurchase
 * @returns {number}
 */
function calculateSumOfPayments(deal, isPurchase) {
  const monthlyPayment = get(deal, 'dealOffer.monthlyPayment');
  const firstPayment = isPurchase ? get(deal, 'dealOffer.downPayment') : get(deal, 'dealOffer.dueAtSigning');
  const term = get(deal, 'dealOffer.term');

  return term * monthlyPayment + firstPayment;
}

/**
 * Returns deal offer.
 * @param {string} dealType
 * @param {number} sellingPrice
 * @param {object} paymentData
 * @param {number} creditScore
 * @return {object}
 */
export function getDealOffer({ dealType, sellingPrice, paymentData, creditScore }) {
  const { dmvFeesItemized, ...fees } = get(paymentData, 'paymentWithTaxes.capitalizedFees', {});

  return {
    dealOffer: {
      ...paymentData,
      fees,
      creditScore,
      sellingPrice,
      taxes: get(paymentData, 'paymentWithTaxes.capitalizedTaxes', {}),
      dealType,
      monthlyPayment: get(paymentData, 'paymentWithTaxes.monthlyPayment'),
      baseMonthlyPayment: get(paymentData, 'paymentWithoutTaxes.baseMonthlyPayment'),
    },
  };
}

/**
 * Adjusts passed deal with calculated data, inventory, and vehicle image.
 * @param {object} deal
 * @param {object} vehicle
 * @param {object} baseDeal
 * @param {string} vehicleImage
 * @param {number} baseSOP
 * @param {object} calculatorSelections
 * @param {boolean} isPurchase
 * @return {object}
 */
function adjustDeal({ deal, vehicle, vehicleImage, baseSOP, baseDeal, calculatorSelections, isPurchase }) {
  return {
    ...deal,
    calculatedData: {
      dealSavings: baseSOP - calculateSumOfPayments(deal, isPurchase),
      msrpMonthlyPayment: get(baseDeal, 'dealOffer.monthlyPayment'),
      msrpBaseMonthlyPayment: get(baseDeal, 'dealOffer.baseMonthlyPayment'),
      tradeInDetails: getTradeInDetails({
        selections: calculatorSelections,
        limitCriteria: get(deal, 'dealOffer.limitCriteria', {}),
        appliedTradeIn: get(deal, 'dealOffer.tradeIn'),
        pubState: vehicle.type,
      }),
    },
    inventory: vehicle,
    photo: vehicleImage,
  };
}

const addSubtractedPrimaryIncentivesAmount = ({ prices, primaryIncentives }) => {
  const primaryIncentivesSum = getTotalRebates({ incentives: primaryIncentives, showLeasePayments: false });

  return Object.fromEntries(
    Object.entries(prices).map(([priceKey, priceValue]) => [priceKey, priceValue + primaryIncentivesSum])
  );
};

/**
 * Gets deal prices based on max great, good, and fair prices.
 * @param {object} vehicle
 * @param {boolean} isLeasePaymentFocused
 * @param {Array} primaryIncentives
 * @param {Array} selectedIncentives
 * @return {object[]}
 */
export function getDealPrices({ vehicle, isLeasePaymentFocused, primaryIncentives, selectedIncentives }) {
  const priceValidation = addSubtractedPrimaryIncentivesAmount({
    prices: pick(get(vehicle, 'computedDisplayInfo.priceValidation', {}), [
      'maxGreatPrice',
      'maxGoodPrice',
      'maxFairPrice',
    ]),
    primaryIncentives,
  });
  const advertisedPrice = get(vehicle, 'prices.advertisedPrice', 0);
  const displayPrice = isLeasePaymentFocused
    ? advertisedPrice ||
      getEdmundsSuggestedPrice({
        vinData: vehicle,
        selectedIncentives,
        primaryLoanIncentives: primaryIncentives,
        showLeasePayments: isLeasePaymentFocused,
      })
    : advertisedPrice;
  const maxGoodPrice = get(priceValidation, 'maxGoodPrice');
  // Need to calculate maxGreatPrice and maxFairPrice for price checker when there is only maxGoodPrice available
  const maxGreatPrice = get(
    priceValidation,
    'maxGreatPrice',
    maxGoodPrice ? Number((maxGoodPrice * 0.97).toFixed(2)) : null
  );
  const maxFairPrice = get(
    priceValidation,
    'maxFairPrice',
    maxGoodPrice ? Number((maxGoodPrice * 1.03).toFixed(2)) : null
  );

  // trying to get the closest value
  const prices = {
    maxGreatPrice: maxGreatPrice || maxGoodPrice || maxFairPrice || displayPrice,
    maxGoodPrice: maxGoodPrice || maxFairPrice || displayPrice,
    maxFairPrice: maxFairPrice || displayPrice,
  };

  return [
    { price: prices.maxGreatPrice, type: 'great' },
    { price: prices.maxGoodPrice, type: 'good' },
    { price: prices.maxFairPrice, type: 'fair' },
    { price: displayPrice, type: 'msrp' },
  ];
}

/**
 * Gets deal offers combined by offer type.
 * @param {object} vehicle
 * @param {string} photoUrl
 * @param {object} purchasePayments
 * @param {object} leasePayments
 * @param {object} calculatorSelections
 * @param {Array} primaryIncentives
 * @param {Array} selectedIncentives
 * @return {object}
 */
export function getDealsByType({
  vehicle,
  photoUrl,
  purchasePayments,
  leasePayments,
  calculatorSelections,
  primaryIncentives,
  selectedIncentives,
  isLeasePaymentFocused,
}) {
  const dealPrices = getDealPrices({
    vehicle,
    primaryIncentives,
    selectedIncentives,
    isLeasePaymentFocused,
  });

  const dealsByType = {
    [PURCHASE]: purchasePayments
      ? dealPrices.map(({ price, type }, index) =>
          getDealOffer({
            dealType: type,
            sellingPrice: price,
            paymentData: purchasePayments[index],
            creditScore: calculatorSelections[PURCHASE].creditScore,
          })
        )
      : [],
    [LEASE]: leasePayments
      ? dealPrices.map(({ price, type }, index) =>
          getDealOffer({
            dealType: type,
            sellingPrice: price,
            paymentData: leasePayments[index],
            creditScore: calculatorSelections[LEASE].creditScore,
          })
        )
      : [],
  };

  const basePurchaseDeal = find(dealsByType[PURCHASE], deal => get(deal, 'dealOffer.dealType') === 'msrp');
  const baseLeaseDeal = find(dealsByType[LEASE], deal => get(deal, 'dealOffer.dealType') === 'msrp');

  const basePurchaseSOP = calculateSumOfPayments(basePurchaseDeal, true);
  const baseLeaseSOP = calculateSumOfPayments(baseLeaseDeal, false);

  const vehicleImage = photoUrl || DEFAULT_PHOTO;

  return {
    [PURCHASE]: dealsByType[PURCHASE].map(deal =>
      adjustDeal({
        deal,
        vehicle,
        vehicleImage,
        baseDeal: basePurchaseDeal,
        baseSOP: basePurchaseSOP,
        isPurchase: true,
        calculatorSelections: calculatorSelections[PURCHASE],
      })
    ),
    [LEASE]: dealsByType[LEASE].map(deal =>
      adjustDeal({
        deal,
        vehicle,
        vehicleImage,
        baseDeal: baseLeaseDeal,
        baseSOP: baseLeaseSOP,
        isPurchase: false,
        calculatorSelections: calculatorSelections[LEASE],
      })
    ),
  };
}
